"use client";

import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { useNotificationSetupBannerContext } from "@/contexts/NotificationSetupBannerContext";
import { useMarketPushReminderEligible } from "@/lib/hooks/useMarketPushReminderEligible";
import { resolveMarketNotificationSurface } from "@/lib/notifications/market-notification-surface";
import MarketIntroBanner from "@/components/onboarding/MarketIntroBanner";
import MarketNearMePushReminder from "@/components/market/MarketNearMePushReminder";

type MarketNotificationBannerSlotProps = {
  nearMePlatformEnabled: boolean;
};

export default function MarketNotificationBannerSlot({
  nearMePlatformEnabled,
}: MarketNotificationBannerSlotProps) {
  const { user } = useAuth();
  const { isPromptOpen } = useOnboarding();
  const setupBanner = useNotificationSetupBannerContext();
  const { eligible: pushReminderEligible, prefsLoaded } =
    useMarketPushReminderEligible(nearMePlatformEnabled);

  const surface = useMemo(() => {
    if (!user?.id || !prefsLoaded) {
      return null;
    }

    return resolveMarketNotificationSurface({
      marketIntroOpen: isPromptOpen("marketIntro"),
      globalSetupBannerVisible: setupBanner?.visible ?? false,
      globalSetupBannerMode: setupBanner?.mode ?? null,
      pushReminderEligible,
    });
  }, [
    isPromptOpen,
    prefsLoaded,
    pushReminderEligible,
    setupBanner?.mode,
    setupBanner?.visible,
    user?.id,
  ]);

  if (!user?.id || !surface) {
    return null;
  }

  if (surface.localBanner === "welcome") {
    return <MarketIntroBanner includeAlertsLine={nearMePlatformEnabled} />;
  }

  if (surface.localBanner === "push_reminder") {
    return (
      <MarketNearMePushReminder nearMePlatformEnabled={nearMePlatformEnabled} />
    );
  }

  return null;
}
